import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaCopy,
  FaShareAlt,
  FaStore,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaSignInAlt,
} from 'react-icons/fa';
import { getMyBusinessReferences } from '../../../../redux/modules/profile/profile.action';

/**
 * Bayi satış temsilcisi (USER) profili: "İşletmeni Getir".
 * Temsilci kendi referans kodunu paylaşır; işletme kayıt formunda
 * "Bizi Nereden Duydunuz -> Satış Temsilcisi" seçip bu kodu girer.
 * Puan kazanımı yoktur; sayfa yalnızca kayıt takibi içindir.
 */
const MyBusinessReferences = () => {
  const dispatch = useDispatch();
  const info = useSelector((store) => store.profile.myBusinessReferences);

  useEffect(() => {
    dispatch(getMyBusinessReferences());
  }, [dispatch]);

  const referenceCode = info?.referenceCode;
  const businesses = info?.businesses || [];
  const totalBusinesses = info?.totalBusinesses || 0;
  const confirmedCount = info?.confirmedCount || 0;
  const waitingCount = info?.waitingCount || 0;
  const rejectedCount = info?.rejectedCount || 0;
  const loggedInCount = info?.loggedInCount || 0;

  const handleCopy = () => {
    if (referenceCode) {
      navigator.clipboard
        .writeText(referenceCode)
        .then(() => toast.success('Referans kodunuz kopyalandı!'))
        .catch(() => toast.error('Kopyalama işlemi başarısız oldu.'));
    }
  };

  const handleShare = async () => {
    if (navigator.share && referenceCode) {
      try {
        await navigator.share({
          title: 'İşletmeni Getir',
          text: `Kayıt sırasında Bizi Nereden Duydunuz alanında Satış Temsilcisi seçip şu referans kodunu girebilirsiniz: ${referenceCode}`,
        });
      } catch (error) {
        console.error('Paylaşım hatası:', error);
      }
    } else {
      toast.error(
        'Tarayıcınız paylaşım özelliğini desteklemiyor. Lütfen kodu kopyalayarak paylaşın.'
      );
    }
  };

  /**
   * Tabloda gösterilen durum, kayıt statüsünün üzerine "Giriş Yaptı" adımını ekler:
   * onaylanan işletme sisteme en az bir kez başarılı giriş yapınca bu duruma geçer.
   */
  const getDisplayStatus = (item) =>
    item.loggedIn ? 'LOGGED_IN' : item.status;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'LOGGED_IN':
        return <FaSignInAlt style={{ color: '#28a745', marginRight: 6, fontSize: 16 }} />;
      case 'CONFIRM':
        return <FaCheckCircle style={{ color: '#0d6efd', marginRight: 6, fontSize: 16 }} />;
      case 'WAITING':
        return <FaHourglassHalf style={{ color: '#ffc107', marginRight: 6, fontSize: 16 }} />;
      case 'REJECTED':
        return <FaTimesCircle style={{ color: '#dc3545', marginRight: 6, fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'LOGGED_IN':
        return 'Giriş Yaptı';
      case 'CONFIRM':
        return 'Onaylandı';
      case 'WAITING':
        return 'Onay Bekliyor';
      case 'REJECTED':
        return 'Reddedildi';
      default:
        return status || '—';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'LOGGED_IN':
        return '#28a745';
      case 'CONFIRM':
        return '#0d6efd';
      case 'WAITING':
        return '#ffc107';
      case 'REJECTED':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm border border-gray-100 p-4">
      <div className="border-bottom pb-3 mb-3">
        <h4 className="fw-bold mb-0 text-dark">İşletmeni Getir</h4>
      </div>

      <div className="text-center py-3 px-md-3">
        <div className="mb-2">
          <FaStore className="text-primary" style={{ fontSize: '36px' }} />
        </div>

        <h6 className="fw-bold mb-2">İşletmeleri platforma siz kazandırın!</h6>
        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
          Referans kodunuzu işletmelerle paylaşın. İşletme üye olurken{' '}
          <span className="fw-semibold">Bizi Nereden Duydunuz?</span> alanında{' '}
          <span className="fw-semibold">Satış Temsilcisi</span> seçip bu kodu girdiğinde kayıt
          size bağlanır ve aşağıdaki listede görünür. İşletme onaylandıktan sonra sisteme ilk kez
          giriş yaptığında durumu <span className="fw-semibold">Giriş Yaptı</span> olarak güncellenir.
        </p>

        {referenceCode ? (
          <div className="d-flex flex-column align-items-center">
            <div
              className="bg-light p-3 rounded-3 mb-3 w-100"
              style={{ maxWidth: '350px', border: '2px dashed #dee2e6' }}
            >
              <span className="text-muted d-block mb-1" style={{ fontSize: '13px' }}>
                Referans Kodum
              </span>
              <span
                className="fw-bold text-primary"
                style={{ fontSize: '28px', letterSpacing: '4px' }}
              >
                {referenceCode}
              </span>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
              <button
                onClick={handleCopy}
                className="btn btn-outline-primary px-3 py-2 d-flex align-items-center gap-2 fw-bold"
                style={{ fontSize: '14px' }}
              >
                <FaCopy /> Kopyala
              </button>

              <button
                onClick={handleShare}
                className="btn btn-primary px-3 py-2 d-flex align-items-center gap-2 fw-bold"
                style={{ fontSize: '14px' }}
              >
                <FaShareAlt /> Paylaş
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-info" role="alert">
            Referans kodunuz oluşturuluyor veya şu anda kullanılamıyor. Lütfen sayfayı yenileyiniz.
          </div>
        )}
      </div>

      {/* Getirilen işletmeler */}
      <div className="border-top pt-3">
        {/*
          Sayaçlar ayrıktır: giriş yapan işletme "Onaylanan" içinde tekrar sayılmaz,
          dördünün toplamı "Toplam İşletme" değerini verir.
        */}
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-4 col-xl">
            <div className="text-center p-2 rounded-3 h-100" style={{ background: '#FFFBEB' }}>
              <div className="fw-bold" style={{ fontSize: 20, color: '#B45309' }}>
                {waitingCount}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Onay Bekleyen
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="text-center p-2 rounded-3 h-100" style={{ background: '#FEF2F2' }}>
              <div className="fw-bold" style={{ fontSize: 20, color: '#dc3545' }}>
                {rejectedCount}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Reddedilen
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="text-center p-2 rounded-3 h-100" style={{ background: '#EFF6FF' }}>
              <div className="fw-bold" style={{ fontSize: 20, color: '#0d6efd' }}>
                {confirmedCount}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Onaylanan
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="text-center p-2 rounded-3 h-100" style={{ background: '#ECFDF5' }}>
              <div className="fw-bold" style={{ fontSize: 20, color: '#059669' }}>
                {loggedInCount}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Giriş Yapan
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl">
            <div className="text-center p-2 rounded-3 h-100" style={{ background: '#F1F5F9' }}>
              <div className="fw-bold" style={{ fontSize: 20, color: '#1E293B' }}>
                {totalBusinesses}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Toplam İşletme
              </div>
            </div>
          </div>
        </div>

        {businesses.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0" style={{ fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#0d6efd', color: '#fff' }}>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>İşletme</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Yetkili</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>İl</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Kayıt Tarihi</th>
                  <th style={{ padding: '10px 12px', fontWeight: 600 }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                      {item.businessName || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                      {item.ownerName || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                      {item.city || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                      {item.registerDate || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                      <div className="d-flex align-items-center">
                        {getStatusIcon(getDisplayStatus(item))}
                        <span
                          style={{
                            color: getStatusColor(getDisplayStatus(item)),
                            fontWeight: 500,
                          }}
                        >
                          {getStatusLabel(getDisplayStatus(item))}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="text-center text-muted fst-italic p-4 rounded-3"
            style={{ border: '1.5px dashed #E2E8F0', fontSize: 13 }}
          >
            <FaStore className="me-2" />
            Henüz kodunuzla kayıt olmuş bir işletme yok.
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBusinessReferences;
